/**
 * The listing field contract (P25009-470): which keys a listing's publicData
 * may hold, and the one gate every write path sends a listing through.
 *
 * Before this, nothing in the system said what a listing field is. The
 * wizard, the CSV import, the supplier feeds and the package publisher each
 * invented their own answer, nothing checked a value before it was stored,
 * and the read side learned to accept every spelling that resulted ("114.3"
 * and "114_3" for one bolt circle). This module is the one answer:
 *
 *   - Console's listing fields are the registry for every field Console
 *     defines. They are always read from Console — the browser's hosted
 *     config, the server's consoleListingConfig — never typed into the code.
 *     A snapshot per marketplace is committed under `marketplaces/` so the
 *     build notices when Console and the code drift apart
 *     (scripts/checkFieldRegistry.js).
 *   - SYSTEM_KEYS are the keys the code itself owns: the search index, the
 *     sales unit, freight, package and storage bookkeeping. Console has no
 *     field for them and needs none.
 *   - gateListingPublicData is the gate. It rewrites each Console value to
 *     the option key Console stores ("114,3" -> `114_3`, "Kumho" ->
 *     `kumhoTire`), rejects a value Console has no option for, naming the
 *     field and the value, and refuses a key that is in neither list.
 *
 * Written as CommonJS on purpose, like boltCircleCore.js: the browser bundle
 * and the plain-Node API server share it, and the server is not transpiled.
 */

const { boltCircleMm, boltCircleOptionKey } = require('./boltCircleCore');

const clean = value => (value === null || value === undefined ? '' : String(value).trim());

/**
 * A comparison key: lower case, no spacing or punctuation, no diacritics, so
 * "Sommardäck", "sommar-dack" and "SOMMARDACK" are one key. The import's
 * value parser uses the same function (importValueParse.normalizeKey).
 */
const normalizeKey = value =>
  String(value === null || value === undefined ? '' : value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[\s\-_./]/g, '')
    .replace(/[^a-z0-9]/g, '');

/* ---------------------------------------------------------------------------
 * The keys the code owns
 * ------------------------------------------------------------------------ */

/**
 * Every publicData key a write path may store that is NOT a Console listing
 * field, by what it is for. A key missing from both this list and Console is
 * refused by the gate — that is how a misspelling (`tireType` for
 * `tiretype`, `sasong` for `tiretype`) stops reaching listings.
 *
 * Adding a key here is a decision that the code owns it. A value a seller or
 * a supplier chooses from a list belongs in Console instead, so it gets
 * options, a label and a search schema.
 */
const SYSTEM_KEY_GROUPS = {
  // Which listing type, and which transaction process it runs on.
  listingType: ['listingType', 'transactionProcessAlias', 'unitType', 'sellerType'],
  /* The car. categoryLevel1/2 are Console CATEGORY ids (listing-categories
   * .json), checked by consoleKeys.matchCarMake/Model, not listing fields. */
  car: [
    'categoryLevel1',
    'categoryLevel2',
    'carMake',
    'carModel',
    'carYear',
    'carName',
    'motor',
    'regPlate',
    'carRegistrationNumber',
  ],
  // The search index strings (listingCombination.js) and free-text keywords.
  searchIndex: ['combination', 'keywords'],
  // Product identifiers the price/stock sync and the Shopping feed match on.
  identity: ['ean', 'supplierArticleNumber'],
  // What the price covers, and when price or stock last moved.
  salesUnit: ['salesUnit', 'setSize', 'salesUnitConfirmed', 'priceStockUpdatedAt', 'updateDate'],
  delivery: [
    'pickupEnabled',
    'shippingEnabled',
    'shippingPriceInSubunitsOneItem',
    'shippingPriceInSubunitsAdditionalItems',
    'location',
    'warehouse',
    'city',
    'orderOnly',
    'deliveryDays',
  ],
  // Text the seller writes, and the stamps that say a text was generated.
  content: [
    'contentGeneratedTitle',
    'contentGeneratedDescription',
    'contentVersion',
    'internalComment',
    'subDescription',
    'externalLink',
    'comment',
  ],
  contact: ['showPhone', 'showEmail', 'allowMessages'],
  // Supplier feeds: where the listing came from and the tyre's own facts.
  supplier: [
    'visibility',
    'isSupplierFeed',
    'supplierFeedId',
    'supplierName',
    'loadIndex',
    'speedIndex',
    'tireModel',
    'rimModel',
  ],
  // Bygg paket (packageReachPublisher).
  package: [
    'isReachShell',
    'packageType',
    'isStaggeredPackage',
    'reachBundle',
    'reachJobId',
    'vehicleKey',
    'packageFitmentLabel',
    'renderedImage',
  ],
  // Förvaring, Inbyte and hidden listings.
  storage: [
    'listingMode',
    'storageType',
    'storageStatus',
    'storageCheckedInAt',
    'storageWarehouseId',
    'storagePosition',
    'storageSeason',
    'storageNumber',
    'storageTerminatedAt',
    'wheelPositions',
    'isTradein',
    'tradeinAt',
    'isHide',
  ],
  // Kept up to date by the server (crons, delete, reservations, reviews).
  server: [
    'isDeleted',
    'deletionReason',
    'totalViews',
    'totalLikes',
    'isReserved',
    'reservationHistoryCount',
    'numberOfReviewers',
    'reviewScore',
    'quantity',
  ],
  /* A Console field on one marketplace only. `tpms` is a Live field (Ja/Nej)
   * that Test does not have; the wizard writes it on both. Where Console has
   * the field, Console's options decide — this entry only stops the key being
   * refused where it does not. */
  oneMarketplaceOnly: ['tpms'],
};

const SYSTEM_KEYS = new Set(Object.values(SYSTEM_KEY_GROUPS).flat());

/* ---------------------------------------------------------------------------
 * The registry: Console's listing fields, indexed
 * ------------------------------------------------------------------------ */

/**
 * One Console listing field in the shape the gate reads. Accepts both the
 * hosted asset (server, `listing-fields.json`) and the browser's merged
 * config (`config.listing.listingFields`), which use the same keys.
 */
const fieldFromConfig = field => {
  const typeConfig = field.listingTypeConfig || {};
  return {
    key: field.key,
    label: clean(field.label) || clean(field.showConfig?.label) || field.key,
    schemaType: field.schemaType,
    options: (field.enumOptions || []).map(option => ({
      option: `${option.option}`,
      label: clean(option.label),
    })),
    listingTypeIds: typeConfig.limitToListingTypeIds ? typeConfig.listingTypeIds || [] : null,
    minimum: field.numberConfig?.minimum,
    maximum: field.numberConfig?.maximum,
  };
};

/**
 * Index Console's public listing fields: options, the listing types each
 * applies to, and the number range of a `long` field. Only `public` fields:
 * they are the ones stored in publicData.
 *
 * @param {Array} listingFields `listing-fields.json` → `listingFields`, or
 *   `config.listing.listingFields` in the browser
 * @returns {{fields: Map<string, Object>}}
 */
const buildFieldRegistry = (listingFields = []) => {
  const fields = new Map();
  (listingFields || []).forEach(field => {
    if (!field || !field.key || (field.scope && field.scope !== 'public')) return;
    fields.set(field.key, fieldFromConfig(field));
  });
  return { fields };
};

/** True when the key is a Console field or a key the code owns. */
const isRegisteredKey = (registry, key) => !!registry?.fields.has(key) || SYSTEM_KEYS.has(key);

const appliesTo = (field, listingType) =>
  !field.listingTypeIds || !listingType || field.listingTypeIds.includes(listingType);

/* ---------------------------------------------------------------------------
 * Values
 * ------------------------------------------------------------------------ */

/* A number written as text, or null. "114,3" and "114.3" are both 114.3. */
const numberOf = value => {
  const text = clean(value).replace(',', '.');
  return /^-?\d+(\.\d+)?$/.test(text) ? Number(text) : null;
};

/**
 * The option key Console uses for a value, or null when there is none.
 *
 * Exact first. Otherwise a number matches the option that MEANS the same
 * number — by the option key, which writes the decimal point as "_"
 * ("114.3" -> `114_3`), or by the label, which is the only place some keys
 * can be read from (`1397` is labelled "139.7"). Text matches the key or the
 * label ignoring case and punctuation ("GT Radial" -> `gtradial`,
 * "Begagnade - Ok skick" -> `usedok`). Two candidates are no match: picking
 * one would be a guess.
 *
 * A bolt circle is read by boltCircleCore (P25009-488): it also has spellings
 * that are no plain number — `1397` and "1143" with the point lost, the
 * trade's "114" for 114.3.
 */
const toConsoleOption = (field, value) => {
  const text = clean(value);
  if (!field || !text) return null;

  const exact = field.options.find(option => option.option === text);
  if (exact) return exact.option;

  if (field.key === 'boltCircle' && boltCircleMm(text) !== null) {
    return boltCircleOptionKey(text, field.options);
  }

  /* A hub bore with its decimal point lost: "661" is 66.1 mm, "841" is 84.1.
   * No hub bore is wider than 161 mm (Console's widest option), so a whole
   * number from 162 to 999 can only be that. Seen on 38 Live listings. */
  if (field.key === 'hubHole' && /^\d{3}$/.test(text) && Number(text) > 161) {
    return toConsoleOption(field, String(Number(text) / 10));
  }

  const number = numberOf(text);
  const candidates = field.options.filter(option =>
    number !== null
      ? numberOf(option.option.replace('_', '.')) === number || numberOf(option.label) === number
      : normalizeKey(option.option) === normalizeKey(text) ||
        normalizeKey(option.label) === normalizeKey(text)
  );
  return candidates.length === 1 ? candidates[0].option : null;
};

/**
 * Offsets are whole millimetres in Console (`long`). Christofer decided
 * (P25009-481 Q9, 2026-10-15) that a decimal ET is stored rounded down —
 * "50,5" is ET 50 — on every write path, not refused. Rounded toward zero,
 * the same as the CSV parser's parseIntStrict, so every path agrees.
 */
const ROUNDED_LONG_FIELDS = new Set(['etFront', 'etRear']);

/**
 * An ET in the whole millimetres Console stores, for the write paths to apply
 * where they BUILD the value: the search strings are built from the same
 * number ("19 ET50" in `combination`), and the gate rounding it afterwards
 * would leave them saying "ET50.5". Anything that is not a finite number is
 * returned as it is, for the gate to judge.
 */
const wholeEt = value =>
  typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : value;

/**
 * A `long` field's value as the integer Console stores, or null when it is
 * none. Numbers written as text count ("45", "-5"), and so does "n15": the
 * option key Console's old ET dropdown used for -15.
 */
const toConsoleLong = (field, value) => {
  let number = null;
  if (typeof value === 'number') {
    number = value;
  } else {
    const text = clean(value);
    const negativeKey = /^n(\d+)$/i.exec(text);
    number = negativeKey ? -Number(negativeKey[1]) : numberOf(text);
  }
  if (number === null || !Number.isFinite(number)) return null;
  if (!Number.isInteger(number)) {
    if (!ROUNDED_LONG_FIELDS.has(field.key)) return null;
    number = Math.trunc(number);
  }
  if (field.minimum !== undefined && field.minimum !== null && number < field.minimum) return null;
  if (field.maximum !== undefined && field.maximum !== null && number > field.maximum) return null;
  return number;
};

/**
 * A brand Console does not have is stored as its `other` option ("Annat"),
 * not refused. That is where most listings sit (P25009-478), and a supplier
 * selling a brand nobody has added to Console yet should still reach the
 * marketplace. The gate says so in a note. Any other field with no option is
 * refused.
 */
const FALLBACK_OPTIONS = { brand: 'other' };

const fallbackOption = field => {
  const fallback = FALLBACK_OPTIONS[field.key];
  return fallback && field.options.some(option => option.option === fallback) ? fallback : null;
};

const valueText = value => (Array.isArray(value) ? value.join(', ') : String(value));

const noOptionIssue = (field, value) => ({
  kind: 'noOption',
  field: field.key,
  label: field.label,
  value: valueText(value),
  reason: `${field.label}: "${Array.isArray(value) ? value.join('", "') : value}" saknar alternativ i Console.`,
});

const notIntegerIssue = (field, value) => {
  const range =
    field.minimum !== undefined && field.maximum !== undefined
      ? ` (${field.minimum} till ${field.maximum})`
      : '';
  return {
    kind: 'notInteger',
    field: field.key,
    label: field.label,
    value: valueText(value),
    reason: `${field.label}: ${value} är inget heltal i det intervall Console tillåter${range}.`,
  };
};

const unknownKeyIssue = (key, value) => ({
  kind: 'unknownKey',
  field: key,
  label: key,
  value: typeof value === 'object' ? JSON.stringify(value) : String(value),
  reason: `Fältet "${key}" finns inte i annonsens fältregister och sparas inte.`,
});

/* One Console field's value: {value} to store, or {issue}. */
const gateFieldValue = (field, value) => {
  if (field.schemaType === 'enum') {
    const option = toConsoleOption(field, value);
    if (option !== null) return { value: option };
    const fallback = fallbackOption(field);
    if (fallback) {
      return {
        value: fallback,
        note: `${field.label}: "${value}" finns inte i Console och sparas som "${fallback}".`,
      };
    }
    return { issue: noOptionIssue(field, value) };
  }

  if (field.schemaType === 'multi-enum') {
    const values = Array.isArray(value) ? value : [value];
    const options = values.map(entry => toConsoleOption(field, entry));
    const missing = values.filter((entry, i) => options[i] === null);
    return missing.length > 0 ? { issue: noOptionIssue(field, missing) } : { value: options };
  }

  if (field.schemaType === 'long') {
    const number = toConsoleLong(field, value);
    return number === null ? { issue: notIntegerIssue(field, value) } : { value: number };
  }

  // text / shortText: free text, stored as written.
  return { value };
};

/**
 * The gate (BR-2). Every write path sends the publicData it is about to
 * store through this, and stores the result only when `issues` is empty.
 *
 *   - A Console field's value is rewritten to Console's option key
 *     ("114,3" -> `114_3`), a `long` to its integer.
 *   - A value Console has no option for is an issue naming the field and
 *     the value. So is a `long` that is no integer in Console's range.
 *   - A key that is neither a Console field nor in SYSTEM_KEYS is an issue
 *     and is left out of the result.
 *   - A Console field that does not apply to this listing type is left
 *     exactly as written: which fields a type carries is
 *     listingTypeFieldsCore's rule (P25009-469), not this one.
 *   - `null` is always allowed, for any key: it removes a value, it cannot
 *     store a wrong one. `undefined` is dropped (it is never sent).
 *
 * @param {Object} params
 * @param {Object} params.publicData what the write path is about to store
 * @param {string} [params.listingType] defaults to publicData.listingType
 * @param {{fields: Map}} params.registry buildFieldRegistry's result
 * @returns {{publicData: Object, issues: Array<{kind, field, label, value,
 *   reason}>, notes: string[]}}
 */
const gateListingPublicData = ({ publicData = {}, listingType, registry }) => {
  const source = publicData || {};
  const type = listingType || source.listingType;
  const result = {};
  const issues = [];
  const notes = [];

  Object.keys(source).forEach(key => {
    const value = source[key];
    if (value === undefined) return;

    const field = registry?.fields.get(key);
    if (!field && !SYSTEM_KEYS.has(key)) {
      if (value === null) {
        result[key] = null;
      } else {
        issues.push(unknownKeyIssue(key, value));
      }
      return;
    }

    if (!field || value === null || value === '' || !appliesTo(field, type)) {
      result[key] = value;
      return;
    }

    const checked = gateFieldValue(field, value);
    if (checked.issue) {
      issues.push(checked.issue);
      return;
    }
    result[key] = checked.value;
    if (checked.note) notes.push(checked.note);
  });

  return { publicData: result, issues, notes };
};

module.exports = {
  SYSTEM_KEY_GROUPS,
  SYSTEM_KEYS,
  FALLBACK_OPTIONS,
  normalizeKey,
  buildFieldRegistry,
  isRegisteredKey,
  toConsoleOption,
  toConsoleLong,
  wholeEt,
  gateListingPublicData,
};
